import { useLanguage } from '../context/LanguageContext';
import { useAdmin } from '../context/AdminContext';
import { t } from '../lib/translations';
import { Button } from './ui/button';
import { Globe, History, Calculator, Users, LogOut, Mail } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_420a1fc0-c22b-4adc-97e3-0401a469efb3/artifacts/nomkxozs_Kurdynowski%20%282%29.png';

export default function Layout({ children }) {
  const { language, toggleLanguage } = useLanguage();
  const { isAdmin, logout } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F9FA]">
      <header
        data-testid="app-header"
        className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
            data-testid="logo-link"
          >
            <img src={LOGO_URL} alt="Kurdynowski" className="h-10 w-auto" />
            <div className="hidden sm:block">
              <div className="text-[#1B3A7B] font-heading text-lg font-bold leading-tight tracking-tight">
                KURDYNOWSKI
              </div>
              <div className="text-[#E8941A] text-xs font-medium tracking-wide">
                Accounting & Tax Solutions
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(location.pathname === '/history' ? '/' : '/history')}
                data-testid="nav-toggle-btn"
                className="text-slate-600 hover:text-[#1B3A7B] hover:bg-slate-100 gap-1.5"
              >
                {location.pathname === '/history' ? (
                  <>
                    <Calculator className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('common.calculator', language)}</span>
                  </>
                ) : (
                  <>
                    <History className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('common.history', language)}</span>
                  </>
                )}
              </Button>
            )}

            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/leads')}
                data-testid="nav-leads-btn"
                className="text-slate-600 hover:text-[#1B3A7B] hover:bg-slate-100 gap-1.5"
              >
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'pl' ? 'Leady' : 'Leads'}</span>
              </Button>
            )}

            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { logout(); navigate('/'); }}
                data-testid="admin-logout-btn"
                className="text-slate-600 hover:text-red-600 hover:bg-red-50 gap-1.5"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={toggleLanguage}
              data-testid="language-toggle-btn"
              className="gap-1.5 border-slate-300 hover:border-[#1B3A7B] hover:text-[#1B3A7B]"
            >
              <Globe className="w-4 h-4" />
              {language === 'pl' ? 'EN' : 'PL'}
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer
        data-testid="app-footer"
        className="border-t border-slate-200 bg-white py-8"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mail className="w-4 h-4 text-[#1B3A7B]" />
              <a href="mailto:monika.gawkowska@kurdynowski.pl" className="hover:text-[#1B3A7B] transition-colors" data-testid="footer-contact-email">
                monika.gawkowska@kurdynowski.pl
              </a>
            </div>

            <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1.5" data-testid="footer-legal-links">
              <Link to="/regulamin" className="text-xs text-slate-400 hover:text-[#1B3A7B] transition-colors">
                {language === 'pl' ? 'Regulamin' : 'Terms'}
              </Link>
              <span className="text-xs text-slate-300">|</span>
              <Link to="/polityka-prywatnosci" className="text-xs text-slate-400 hover:text-[#1B3A7B] transition-colors">
                {language === 'pl' ? 'Polityka prywatnosci' : 'Privacy'}
              </Link>
              <span className="text-xs text-slate-300">|</span>
              <Link to="/rodo" className="text-xs text-slate-400 hover:text-[#1B3A7B] transition-colors">
                RODO
              </Link>
              <span className="text-xs text-slate-300">|</span>
              <Link to="/regulamin-platnosci" className="text-xs text-slate-400 hover:text-[#1B3A7B] transition-colors">
                {language === 'pl' ? 'Regulamin platnosci' : 'Payment terms'}
              </Link>
              <span className="text-xs text-slate-300">|</span>
              <Link to="/cookies" className="text-xs text-slate-400 hover:text-[#1B3A7B] transition-colors">
                Cookies
              </Link>
            </nav>

            <div className="text-center space-y-0.5">
              <p className="text-xs text-slate-400">
                &copy; {new Date().getFullYear()} Biuro Rachunkowe Kurdynowski
              </p>
              <p className="text-xs text-slate-400">
                {language === 'pl' ? 'Projekt i wykonanie' : 'Design & development'}: Monika Malgorzata Gawkowska
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
